import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Grid,
  Stack,
  useToast,
  Heading,
  Box,
  Flex,
  Button,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  Select,
  Icon,
  Input,
  Skeleton,
} from "@chakra-ui/react";
import { InputGroup, InputLeftElement } from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";

import { useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment-timezone";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";

const SERVER = import.meta.env.VITE_API_URL;

const statusColor = (status) => {
  if (!status) return "gray";
  const s = String(status).toLowerCase();
  if (s.includes("expired")) return "red";
  return "gray";
};

const History = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [expiredLeads, setExpiredLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [columnFilters, setColumnFilters] = useState({
    name: "",
    mobile: "",
    date: "",
    time: "",
    price: "",
    pickUp: "",
    destination: "",
    car: "",
    status: "",
  });

  const fetchHistoryData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("prima_session");
      if (!token) throw new Error("Token not found");

      // Fetch expired leads
      const expiredUrl = `${SERVER}/management/expired-leads?page=${currentPage}&limit=${itemsPerPage}`;
      const expiredRes = await axios.get(expiredUrl, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      // Fetch completed bookings
      const completedUrl = `${SERVER}/management/completed-bookings?page=${currentPage}&limit=${itemsPerPage}`;
      const completedRes = await axios.get(completedUrl, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      const normalizeData = (data, type) => {
        return data.map((r) => ({
          id: r._id ?? r.id ?? Math.random().toString(36).slice(2, 9),
          name: r.customer?.name ?? r.customerName ?? r.name ?? "-",
          mobileNo:
            r.customer?.mobile ?? r.customer?.mobileNo ?? r.mobileNo ?? "-",
          createdDate:
            r.pickupDateTime ?? r.dateTime ?? r.date ?? r.createdAt ?? "-",
          pickupDate:
            r.pickupDateTime ?? r.dateTime ?? r.date ?? r.createdAt ?? "-",
          date: r.pickupDateTime ?? r.dateTime ?? r.date ?? r.createdAt ?? "-",
          price: r.estimatedFare
            ? `₹${r.estimatedFare}`
            : r.fareDetails?.total
              ? `₹${r.fareDetails.total}`
              : (r.price ?? "-"),
          pickUp:
            typeof (r.fromLocation ?? r.pickupLocation) === "object"
              ? (r.fromLocation ?? r.pickupLocation)?.address || "-"
              : (r.fromLocation ?? r.pickupLocation ?? "-"),
          destination:
            typeof (r.toLocation ?? r.dropLocation) === "object"
              ? (r.toLocation ?? r.dropLocation)?.address || "-"
              : (r.toLocation ?? r.dropLocation ?? "-"),
          car:
            r.vehicleSegment?.segmentName ??
            r.carType?.segmentName ??
            r.car ??
            "-",
          status: r.status ?? "-",
          type: type,
          raw: r,
        }));
      };

      const expiredData = expiredRes.data.data ?? expiredRes.data ?? [];
      const completedData = completedRes.data.data ?? completedRes.data ?? [];

      const normalizedExpired = normalizeData(expiredData, "Expired Lead");
      const normalizedCompleted = normalizeData(
        completedData,
        "Completed Booking",
      );

      // Combine both
      const combinedData = [...normalizedExpired, ...normalizedCompleted];
      setExpiredLeads(combinedData);
      setError(null);
    } catch (err) {
      console.warn(err);
      setError(err?.response?.data?.message ?? err.message ?? "API error");
      toast({
        title: "Error",
        description: "Failed to fetch history",
        status: "error",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistoryData();
  }, [currentPage, itemsPerPage]);

  // Apply column filters
  const filteredLeads = useMemo(() => {
    if (!Object.values(columnFilters).some((v) => v)) {
      return expiredLeads;
    }

    return expiredLeads.filter((item) => {
      const name = (item.name || "").toLowerCase();
      const mobile = (item.mobileNo || "").toLowerCase();
      const price = (item.price || "").toLowerCase();
      const pickUp = (item.pickUp || "").toLowerCase();
      const destination = (item.destination || "").toLowerCase();
      const car = (item.car || "").toLowerCase();
      const status = (item.status || "").toLowerCase();

      // Format date for filtering (same as display format)
      let dateStr = "";
      if (item.date && item.date !== "-") {
        try {
          dateStr = new Date(item.date)
            .toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
            .toLowerCase();
        } catch (e) {
          dateStr = (item.date || "").toLowerCase();
        }
      }

      // Format time for filtering (same as display format)
      let timeStr = "";
      if (item.date && item.date !== "-") {
        try {
          const date = new Date(item.date);
          const hours = date.getHours();
          const minutes = date.getMinutes().toString().padStart(2, "0");
          const ampm = hours >= 12 ? "PM" : "AM";
          const displayHours = hours % 12 || 12;
          timeStr = `${displayHours}:${minutes} ${ampm}`.toLowerCase();
        } catch (e) {
          timeStr = (item.time || "").toLowerCase();
        }
      }

      return (
        name.includes(columnFilters.name.toLowerCase()) &&
        mobile.includes(columnFilters.mobile.toLowerCase()) &&
        dateStr.includes(columnFilters.date.toLowerCase()) &&
        timeStr.includes(columnFilters.time.toLowerCase()) &&
        price.includes(columnFilters.price.toLowerCase()) &&
        pickUp.includes(columnFilters.pickUp.toLowerCase()) &&
        destination.includes(columnFilters.destination.toLowerCase()) &&
        car.includes(columnFilters.car.toLowerCase()) &&
        status.includes(columnFilters.status.toLowerCase())
      );
    });
  }, [expiredLeads, columnFilters]);

  const totalPages = Math.ceil((filteredLeads.length || 0) / itemsPerPage) || 1;

  const SearchTh = ({ value, onChange, placeholder = "Search" }) => (
    <Th p={2}>
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="#8BA3CB" boxSize="3.5" />
        </InputLeftElement>

        <Input
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onClick={(e) => e.stopPropagation()}
          fontSize="xs"
          borderRadius="8px"
          bg="#fff"
          border="none"
          pl="2rem"
          _placeholder={{ color: "#8BA3CB" }}
          _focus={{
            bg: "white",
            boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.3)",
          }}
        />
      </InputGroup>
    </Th>
  );

  return (
    <Stack spacing={6} pb={20} py={6} px={6}>
      <BreadCrumbMaker items={[{ title: "History" }]} />

      {/* <Box>
        <Heading fontSize={"xl"} py={1} color={"#333B69"}>
          HIstory of Expired leads and completed rides
        </Heading>
      </Box> */}

      {/* Table */}
      <Box p={7} bg={"white"} overflowX={"auto"} borderRadius={"3xl"}>
        <Table size="sm" bg={"white"} fontSize={"small"}>
          <Thead>
            <Tr>
              <Th>S.No</Th>
              {/* <Th>Type</Th> */}
              <Th>Name</Th>
              <Th>Mobile No.</Th>
              <Th>Date</Th>
              <Th>Time</Th>
              <Th>Price</Th>
              <Th>Pick Up</Th>
              <Th>Destination</Th>
              <Th>Car</Th>
              <Th>Status</Th>
            </Tr>
            {/* Column Filter Row */}
            <Tr bg="gray.50">
              <Th></Th>
              <SearchTh
                value={columnFilters.name}
                onChange={(e) =>
                  setColumnFilters({ ...columnFilters, name: e.target.value })
                }
              />

              <SearchTh
                value={columnFilters.mobile}
                onChange={(e) =>
                  setColumnFilters({ ...columnFilters, mobile: e.target.value })
                }
              />

              <SearchTh
                value={columnFilters.date}
                onChange={(e) =>
                  setColumnFilters({ ...columnFilters, date: e.target.value })
                }
              />

              <SearchTh
                value={columnFilters.time}
                onChange={(e) =>
                  setColumnFilters({ ...columnFilters, time: e.target.value })
                }
              />

              <SearchTh
                value={columnFilters.price}
                onChange={(e) =>
                  setColumnFilters({ ...columnFilters, price: e.target.value })
                }
              />

              <SearchTh
                value={columnFilters.pickUp}
                onChange={(e) =>
                  setColumnFilters({ ...columnFilters, pickUp: e.target.value })
                }
              />

              <SearchTh
                value={columnFilters.destination}
                onChange={(e) =>
                  setColumnFilters({
                    ...columnFilters,
                    destination: e.target.value,
                  })
                }
              />

              <SearchTh
                value={columnFilters.car}
                onChange={(e) =>
                  setColumnFilters({ ...columnFilters, car: e.target.value })
                }
              />

              <SearchTh
                value={columnFilters.status}
                onChange={(e) =>
                  setColumnFilters({ ...columnFilters, status: e.target.value })
                }
              />

              <Th></Th>
            </Tr>
          </Thead>

          <Tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <Tr key={i}>
                  <Td colSpan={11}>
                    <Skeleton height="32px" borderRadius="md" />
                  </Td>
                </Tr>
              ))
            ) : filteredLeads.length === 0 ? (
              <Tr>
                <Td colSpan={11} textAlign="center" py={8}>
                  <Text color="gray.500">No history records</Text>
                </Td>
              </Tr>
            ) : (
              filteredLeads.map((item, index) => (
                <Tr
                  key={item.id}
                  onClick={() => {
                    const leadId = item.raw?._id || item.id;
                    navigate(`/history/lead/${leadId}`);
                  }}
                  cursor="pointer"
                  _hover={{ bg: "gray.50" }}
                >
                  <Td>{(currentPage - 1) * itemsPerPage + index + 1}</Td>
                  {/* <Td>
                    <Badge colorScheme={item.type === "Expired Lead" ? "red" : "green"} variant="subtle">
                      {item.type}
                    </Badge>
                  </Td> */}
                  <Td>{item.name}</Td>
                  <Td>{item.mobileNo}</Td>
                  <Td>
                    {item.date && item.date !== "-"
                      ? new Date(item.date).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                      : "-"}
                  </Td>
                  <Td>
                    <span style={{ whiteSpace: "nowrap" }}>
                      {(() => {
                        if (!item.date || item.date === "-") return "-";
                        const date = new Date(item.date);
                        const hours = date.getHours();
                        const minutes = date
                          .getMinutes()
                          .toString()
                          .padStart(2, "0");
                        const ampm = hours >= 12 ? "PM" : "AM";
                        const displayHours = hours % 12 || 12;
                        return `${displayHours}:${minutes} ${ampm}`;
                      })()}
                    </span>
                  </Td>
                  <Td>{item.price}</Td>
                  <Td>{item.pickUp}</Td>
                  <Td>{item.destination}</Td>
                  <Td>{item.car}</Td>
                  <Td>
                    <Badge
                      colorScheme={statusColor(item.status)}
                      px={3}
                      py={1}
                      rounded="lg"
                    >
                      {item.status}
                    </Badge>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>

        {/* Pagination */}
        <Flex justifyContent="space-between" alignItems="center" mt={4} gap={2}>
          <Flex alignItems="center" gap={2}>
            <Text fontSize="sm">Rows per page:</Text>
            <Select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              width="80px"
              size="sm"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </Select>
          </Flex>

          <Flex alignItems="center" gap={2}>
            <Button
              size="sm"
              isDisabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            >
              Previous
            </Button>
            <Text fontSize="sm">
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              size="sm"
              isDisabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      </Box>
    </Stack>
  );
};

export default History;
